import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { AssignOdometerReadingResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for assigning an odometer reading to an order
 */
export interface AssignOdometerReadingInput {
	orderId: string;
	odometerReading: number;
}

/**
 * API function to assign an odometer reading to a purchased vehicle
 */
export const assignOdometerReading = async (
	servicesProviderId: string,
	{ orderId, odometerReading }: AssignOdometerReadingInput,
): Promise<AssignOdometerReadingResponse> => {
	const response = await apiClient.post<AssignOdometerReadingResponse>(
		ordersEndpoints.assignOdometerReading(servicesProviderId, orderId),
		{ odometerReading },
	);
	return response.data;
};

/**
 * Hook for assigning an odometer reading to a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useAssignOdometerReading = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: Omit<AssignOdometerReadingInput, 'orderId'>) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to assign odometer reading without an order ID or services provider ID.',
				);
			}

			return assignOdometerReading(servicesProviderId, {
				orderId,
				odometerReading: input.odometerReading,
			});
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.ASSIGN_ODOMETER_READING);
		},
	});
};
